/**
 * Put a deadline on work that has none of its own.
 *
 * **This file imports nothing**, for the same reason `background-permission-
 * route.ts` and `background-attempt-memory.ts` do not: the modules that need a
 * deadline most are the ones wrapping native calls, and those cannot be loaded
 * by a test. A bound that cannot be driven by a test is a bound nobody has
 * checked.
 *
 * ## Why this is shared rather than private
 *
 * It was private, in `location.ts`, written there because
 * "`getCurrentPositionAsync` has no timeout of its own and will wait forever
 * for a fix it may never get ... Without this the screen sits on 'Loading your
 * space' with no way out."
 *
 * That is not a GPS problem. It is the shape of every native call this app
 * makes: the bridge promises to call back and nothing guarantees it ever does.
 * The same hang was then found in the background-permission path, where a
 * wedged keychain read leaves the consent sheet's button disabled and reading
 * "Requesting…" with nothing to press, and a wedged write stops the OS prompt
 * being shown at all. Copying the helper a second time would have been the
 * third copy of a rule this repository has already watched get forgotten at the
 * next call site — the argument `source-anchor.ts` and `code-only.ts` both make.
 *
 * ## The contract
 *
 * Resolves with the work's value, or `null` if it failed OR ran out of time.
 * Never rejects, and never leaves a rejection unhandled: the `.catch` is
 * attached whether or not the timer has already fired, so work that rejects
 * late settles into a no-op instead of an unhandled rejection.
 *
 * `null` deliberately collapses "failed" and "took too long" — every caller
 * here has to do the same safe thing either way, and splitting them would
 * invite a caller to treat one of them as success.
 *
 * The timer is always cleared on the settling path, so a bounded call does not
 * hold a handle open for the rest of the budget.
 */
export function withTimeout<T>(work: Promise<T>, ms: number): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);

    void work
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(null);
      });
  });
}
